/* LinksComponent.js */

"use strict";

const _ = require("lodash");
const DefaultColumnComponent = require("@gluenetworks/steve_ui_widgets").GridGl
    .DefaultColumnComponent;
const Renderer = require("./LinksRenderer");
const disabledStatus = ["DOWNLOADING", "UPLOADING"];
const Registrations = require("../../../../../../../../../RegistrationNamespaces");
const DeleteImageComponent = require("../../veils/deleteImage/DeleteImageComponent");
const EditImageComponent = require("../../veils/editImage/EditImageComponent");

class LinksComponent extends DefaultColumnComponent {
    constructor(entity, context) {
        super(entity);
        this._entity = entity;
        this._context = context;
        this._gameEditor = context.gameEditor;
        this._activeOrgId = this._gameEditor._activeOrgId;
        this._linkStatus = _.get(entity, "linkStatus", "");
        this._isDisabled = false;
        if (_.includes(disabledStatus, this._linkStatus)) {
            this._isDisabled = true;
        }
        this._OdmOsUpgradePlanClass = this._gameEditor._planEntity;
        this._renderer = new Renderer(this);
        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {
        if (this._entity) {
            this._entity.on(
                "LinkStatusPropertyUpdated",
                this._onLinkStatusPropertyUpdated.bind(this)
            );
        }
    }

    _unregisterListeners() {
        if (this._entity) {
            this._entity.removeAllListeners("LinkStatusPropertyUpdated");
        }
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get gameEditor() {
        return this._gameEditor;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        let prom = this._renderer._shutdown();
        this._renderer = undefined;
        this._context = undefined;
        this._gameEditor = undefined;
        this._activeOrgId = undefined;
        this._linkStatus = undefined;
        this._isDisabled = undefined;
        this._entity = undefined;
        this._OdmOsUpgradePlanClass = undefined;
        return prom;
    }

    /******************************************/
    /* START PUBLIC API METHODS               */
    /******************************************/

    setValue() {}

    _onLinkStatusPropertyUpdated() {
        this._linkStatus = this._entity.linkStatus;
        if (_.includes(disabledStatus, this._linkStatus)) {
            this._isDisabled = true;
        } else {
            this._isDisabled = false;
        }
        this.emit("LinkStatusPropertyUpdated");
    }

    /******************************************/
    /* END PUBLIC API METHODS                 */
    /******************************************/

    uploadImage() {
        this._gameEditor._activeView._selectedLinkForUploadImage = this._entity;
        this._gameEditor.runAction(Registrations.IOT_OS_PLAN_EDITOR_ACTIONS_UPLOAD_IMAGE);
    }

    editImage() {
        const logger = this._gameEditor._logger;

        this._gameEditor._activeView._selectedLinkForEditImage = this._entity;
        return this.gameEditor._modalUserFeedback
            .hideDisplayed()
            .then(() => {
                return this.gameEditor._modalUserFeedback.displayCustom(
                    new EditImageComponent(this._entity, logger),
                    {dontHide: true}
                );
            })
            .then((result) => {
                if (result && result.saved) {
                    // Entity has already been updated via setters, just save it
                    return this._entity.update();
                }
                return;
            })
            .then(() => {
                // Refresh the grid row if update was successful
                if (this._entity) {
                    this.emit("EntityUpdated");
                }
                return;
            })
            .catch((err) => {
                if (err && !err.userRejected) {
                    logger.warn(err, "LinksComponent.editImage() error");
                }
                return;
            })
            .finally(() => {
                this.gameEditor._modalUserFeedback.hideDisplayed();
            });
    }

    deleteImage() {
        const planName = this._gameEditor.getPlan().getName();
        const logger = this._gameEditor._logger;
        const osPlan = this._gameEditor.getPlan();
        const modalUserFeedback = this.gameEditor._modalUserFeedback;
        const entityId = this._entity.getId();
        // Capture references early to prevent race condition when component shuts down
        const OdmOsUpgradePlanClass = this._OdmOsUpgradePlanClass;
        const activeOrgId = this._activeOrgId;

        this._gameEditor._activeView._selectedLinkForDeleteImage = this._entity;
        return modalUserFeedback
            .hideDisplayed()
            .then(() => {
                return modalUserFeedback.displayCustom(
                    new DeleteImageComponent(this._entity, planName, logger),
                    {
                        dontHide: true,
                    }
                );
            })
            .then((result) => {
                if (result && result.confirmed) {
                    // Hide the veil BEFORE deleting to prevent empty veil state
                    return modalUserFeedback.hideDisplayed().then(() => {
                        // Merge both updates into a single promise chain to avoid race condition
                        return OdmOsUpgradePlanClass.loadOdmOsUpgradePlan(
                            activeOrgId,
                            osPlan.getId()
                        ).then((plan) => {
                            const planOnlyLinkIds = plan.getPlanOnlyLinkIds();

                            plan.removeFromLinksRelationship(entityId);

                            if (!_.isEmpty(planOnlyLinkIds) && planOnlyLinkIds.includes(entityId)) {
                                plan.setPlanOnlyLinkIds(
                                    planOnlyLinkIds.filter((id) => id !== entityId)
                                );
                            }
                            const ignoreLinkIds = plan.getIgnoreLinkIds();
                            if (!_.isEmpty(ignoreLinkIds) && ignoreLinkIds.includes(entityId)) {
                                plan.setIgnoreLinkIds(
                                    ignoreLinkIds.filter((id) => id !== entityId)
                                );
                            }

                            if (plan.isDirty() || !_.isEmpty(plan.getPendingRelationships())) {
                                return plan.update();
                            }

                            return Promise.resolve();
                        });
                    });
                }
                return Promise.resolve();
            })
            .catch((err) => {
                if (err && !err.userRejected) {
                    logger.warn(err, "LinksComponent.deleteImage() error");
                }
            })
            .finally(() => {
                // Ensure cleanup even on cancel
                modalUserFeedback?.hideDisplayed();
            });
    }
}

module.exports = LinksComponent;
